import { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';
import DashboardHome from '../pages/DashboardHome';
import DeployForm from '../components/DeployForm.js';
import StatusTracker from '../pages/StatusTracker';
import ClusterList from '../pages/ClusterList';
import EdgeCredentials from '../pages/EdgeCredentials';
import DeleteCluster from '../pages/DeleteCluster';
import UsersPage from '../pages/UserList.js';
import useCurrentUser from '../hooks/useCurrentUser';
import { roleHas, CLUSTER_VIEW_CUSTOMER, CLUSTER_CREATE_CUSTOMER, EDGE_CREDS_MANAGE } from '../utils/rolePermissions';
import { LayoutDashboard, Rocket, List, KeyRound, Trash2, Users, LogOut, ChevronLeft, ChevronRight } from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const role = currentUser?.role || '';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  const isActive = (path) => location.pathname.startsWith(`/dashboard/${path}`);

  const handleSignOut = () => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem("activeCluster");
      localStorage.removeItem("activeStarterTab");
      localStorage.removeItem("activeStage");
      sessionStorage.clear();
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  return (
    <div className="dashboard-container">
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <img src="/lightbeam-logo.svg" alt="LightBeam" className="sidebar-logo" />
          <span className="sidebar-title-text">LB Launchpad</span>
        </div>
        <div className="menu-section-label">Navigation</div>
        <ul className="menu">
          {roleHas(role, CLUSTER_VIEW_CUSTOMER) && (
            <li className={isActive('home') ? 'active' : ''} onClick={() => navigate('/dashboard/home')} title="Home">
              <LayoutDashboard size={18} className="menu-icon" />
              <span className="menu-label">Home</span>
            </li>
          )}
          {roleHas(role, CLUSTER_CREATE_CUSTOMER) && (
            <li className={isActive('deploy') ? 'active' : ''} onClick={() => navigate('/dashboard/deploy')} title="Launch Cluster">
              <Rocket size={18} className="menu-icon" />
              <span className="menu-label">Launch Cluster</span>
            </li>
          )}
          {roleHas(role, CLUSTER_VIEW_CUSTOMER) && (
            <li className={isActive('list') ? 'active' : ''} onClick={() => navigate('/dashboard/list')} title="Cluster Overview">
              <List size={18} className="menu-icon" />
              <span className="menu-label">Cluster Overview</span>
            </li>
          )}
          {roleHas(role, EDGE_CREDS_MANAGE) && (
            <li className={isActive('credentials') ? 'active' : ''} onClick={() => navigate('/dashboard/credentials')} title="Edge Credentials">
              <KeyRound size={18} className="menu-icon" />
              <span className="menu-label">Edge Credentials</span>
            </li>
          )}
          {roleHas(role, CLUSTER_VIEW_CUSTOMER) && (
            <li className={isActive('delete') ? 'active' : ''} onClick={() => navigate('/dashboard/delete')} title="Delete Cluster">
              <Trash2 size={18} className="menu-icon" />
              <span className="menu-label">Delete Cluster</span>
            </li>
          )}
          {/* Users: visible to everyone (view-only unless admin) */}
          <li className={isActive('users') ? 'active' : ''} onClick={() => navigate('/dashboard/users')} title="Users">
            <Users size={18} className="menu-icon" />
            <span className="menu-label">Users</span>
          </li>
        </ul>
        <div className="sidebar-footer">
          <div className="footer-item" onClick={handleSignOut} title="Sign Out">
            <LogOut size={18} className="menu-icon" />
            <span className="menu-label">Sign Out</span>
          </div>
          <button className="collapse-btn" onClick={toggleSidebar} title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      </div>

      <div className="content">
        <Routes>
          <Route path="home" element={<DashboardHome />} />
          <Route path="deploy" element={<DeployForm />} />
          <Route path="status" element={<StatusTracker />} />
          <Route path="list" element={<ClusterList />} />
          <Route path="credentials" element={<EdgeCredentials />} />
          <Route path="delete" element={<DeleteCluster />} />
          <Route path="users" element={<UsersPage />} />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;
